import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faSpinner, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { useProcedureExecution } from '../../hooks/useProcedureExecution';
import { useProcedureExecutionStore } from '@/stores/procedure-execution.store';
import {
  PROCEDURE_REQUIREMENT_COPY,
  PROCEDURE_VS_INTAKE_HELP,
} from '@/features/inspection/report/report-workflow';
import { SectionHeader } from '../SectionShell';
import { ExecutionPanelHeader } from './ExecutionPanelHeader';
import { SectionAccordionList } from './SectionAccordionList';
import { CompleteProcedureButton } from './CompleteProcedureButton';

// ── Skeleton ──────────────────────────────────────────────────────────────────

function ProcedurePanelSkeleton() {
  return (
    <div className="space-y-2 p-4 animate-pulse">
      <div className="h-4 w-2/3 rounded bg-muted" />
      <div className="h-2 w-full rounded-full bg-muted" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-10 rounded-lg bg-muted/50" />
      ))}
    </div>
  );
}

// ── No-procedure banner ───────────────────────────────────────────────────────

function NoProcedureAssignedBanner() {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-muted/30 px-4 py-4">
      <FontAwesomeIcon
        icon={faInfoCircle}
        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
      />
      {/* Wording comes from the shared readiness copy so this panel and the
          report readiness card cannot describe the same situation in two
          different ways (Phase 5B step 4). */}
      <div>
        <p className="text-sm font-medium text-foreground">
          {PROCEDURE_REQUIREMENT_COPY.NOT_REQUIRED.label}
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {PROCEDURE_REQUIREMENT_COPY.NOT_REQUIRED.help}
        </p>
        <p className="mt-1 text-xs italic text-muted-foreground">{PROCEDURE_VS_INTAKE_HELP}</p>
      </div>
    </div>
  );
}

// ── Root Panel ────────────────────────────────────────────────────────────────

interface Props {
  inspectionId: string;
  canExecute: boolean;
}

export function ProcedureExecutionPanel({ inspectionId, canExecute }: Props) {
  const panelOpen = useProcedureExecutionStore((s) => s.panelOpen);
  const setPanelOpen = useProcedureExecutionStore((s) => s.setPanelOpen);
  const reset = useProcedureExecutionStore((s) => s.reset);

  useEffect(() => {
    return () => reset();
  }, [inspectionId, reset]);

  const { data: execution, isLoading, error } = useProcedureExecution(inspectionId);

  const is404 =
    error !== null && (error as { response?: { status?: number } })?.response?.status === 404;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      {/* Panel header (always visible) */}
      {isLoading ? (
        <div className="flex items-center gap-3 px-5 py-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-4 w-4 animate-spin text-muted-foreground"
          />
          <span className="text-sm text-muted-foreground">Loading procedure…</span>
        </div>
      ) : is404 || !execution ? (
        <>
          <SectionHeader icon={faListCheck} title="Procedure Execution" />
          <div className="px-5 py-4">
            <NoProcedureAssignedBanner />
          </div>
        </>
      ) : (
        <>
          <ExecutionPanelHeader
            execution={execution}
            isOpen={panelOpen}
            onToggle={() => setPanelOpen(!panelOpen)}
          />

          {panelOpen && (
            <>
              <div className="border-t border-border px-4 pb-2 pt-4">
                {isLoading ? (
                  <ProcedurePanelSkeleton />
                ) : (
                  <SectionAccordionList
                    inspectionId={inspectionId}
                    sections={execution.sections}
                    canExecute={canExecute}
                  />
                )}
              </div>

              <CompleteProcedureButton
                inspectionId={inspectionId}
                isCompleted={execution.status === 'COMPLETED'}
                canExecute={canExecute}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
